import { BaseApiService, ApiResponse, PaginationInfo } from './baseApi';
import { API_ENDPOINTS } from '../config/api';

// Enums
export enum InvoiceStatus {
  DRAFT = 'DRAFT',
  // SENT = 'SENT',
  // PAID = 'PAID',
  // OVERDUE = 'OVERDUE',
  // CANCELLED = 'CANCELLED',
  E_INVOICED = 'E_INVOICED',
  /** Backend cancel e-invoice status */
  E_INVOICE_CANCELLED = 'E_INVOICE_CANCELLED',
  /** Legacy / alternate spelling */
  E_INVOICED_CANCELLED = 'E_INVOICED_CANCELLED',
  E_INVOICE_FAILED = 'E_INVOICE_FAILED'
}

// Basic model interfaces
export interface Branch {
  id: number;
  branchName: string;
  branchCode: string;
  address: string;
  contactPerson: string;
  phone: string;
  email: string;
  isActive: boolean;
  gstin: string;
  stateName: string;
  stateCode: string;
  panNo: string;
  createdAt: string;
  updatedAt: string;
}

export interface Customer {
  id: number;
  customerName: string;
  customerCode: string;
  address: string;
  contactPerson: string;
  phone: string;
  email: string;
  gstin: string;
  stateName: string;
  stateCode: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Endgood {
  id: number;
  endgoodName: string;
  partNumber: string;
  sku: string;
  description: string;
  drawingNumber: string;
  drawingImagePath: string;
  productionWeight: number;
  isActive: boolean;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
}

export interface WorkOrder {
  id: number;
  workOrderNumber: string;
  workOrderDate: string;
  status: string;
  priority: string;
  isActive: boolean;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
}

export interface SalesOrder {
  id: number;
  soNumber: string;
  orderDate: string;
  status: string;
  effectiveStartDate: string;
  effectiveEndDate: string;
  notes: string;
  refNo: string;
  refDate: string;
  totalAmount: number;
  taxAmount: number;
  grandTotal: number;
  isActive: boolean;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
  customer: Customer;
}

// Main invoice interfaces
export interface InvoiceDetails {
  id: number;
  invoiceHeader: InvoiceHeader;
  endgood: Endgood;
  endgoodId?: number; // Direct endgood ID from API response
  endgoodName?: string; // Direct endgood name from API response
  /** ENDGOOD | RM (BOP aliases map to RM on backend) */
  lineItemType?: string;
  rawItemId?: number;
  rawItemName?: string;
  rawItemSku?: string;
  storeId?: number;
  stockPosted?: boolean;
  stockPostedAt?: string;
  heats?: InvoiceDetailHeatDto[];
  uom: string;
  qty: number;
  rate: number;
  hsn: string;
  cgst: number;
  sgst: number;
  igst: number;
  workOrder: WorkOrder;
  workOrderId?: number; // Direct work order ID from API response
  typeOfGood: string;
  boxes: string;
  packingDetailIds?: number[]; // Array of PackingDetail record IDs
  heatNo: string;
  userProvidedHeatNo: string;
  /** True when line was entered as manual goods (maps to DB column is_manual) */
  isManual?: boolean;
  is_manual?: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Description of goods: endgood first, then RM raw item name. */
export function getInvoiceLineDescription(
  line: Pick<InvoiceDetails, 'endgoodName' | 'rawItemName'> & {
    endgood?: { endgoodName?: string } | null;
  }
): string {
  const name =
    String(line.endgoodName ?? '').trim() ||
    String(line.endgood?.endgoodName ?? '').trim() ||
    String(line.rawItemName ?? '').trim();
  return name || '—';
}

export interface InvoiceDetailHeatDto {
  id?: number;
  invoiceDetailId?: number;
  heatNo: string;
  quantity: number;
  storeId?: number | null;
  storeTransactionId?: number | null;
}

/** Mirrors backend InvoiceHeaderDto from invoice service. */
export interface InvoiceHeaderDto {
  id: number;
  branchId?: number;
  /**
   * Effective ship-from branch (falls back to invoice branch when DB ship_from is null).
   * On create/update: omit or null = same as branch.
   */
  shipFrom?: number | null;
  shipFromName?: string | null;
  shipTo?: number;
  billTo: number;
  createDate?: string;
  createdBy?: number;
  approveBy?: number;
  signedBy?: number;
  /** When the invoice was digitally signed (e-invoice); ISO or backend datetime string */
  signedOn?: string;
  invoiceNo: string;
  invoiceDate: string;
  irn?: string;
  ackNo?: string;
  ackDate?: string;
  ewayBillNo?: string;
  deliverNote?: string;
  referenceNoAndDate?: string;
  otherReferences?: string;
  additionalRemarks?: string;
  additional_remarks?: string;
  buyerOrderNo?: string;
  buyerOrderDate?: string;
  dispatchDocNo?: string;
  deliverNoteDate?: string;
  dispatchedThrough?: string;
  destination?: string;
  billOfLanding?: string;
  motorVehicleNo?: string;
  salesOrderId?: number;
  termOfDelivery?: string;
  /** e-invoice supply type (e.g. B2B, SEZWP, SEZWOP, EXPWP, EXPWOP, DEXP) */
  supTyp?: string;
  supplyType?: string;
  sup_typ?: string;
  exportCountryCode?: string;
  exportCountryId?: number;
  /** Buyer / export country master id; may mirror exportCountryId */
  countryId?: number;
  /** Export port (port_details.id) for e-way / export invoices */
  portId?: number;
  portCode?: string;
  portDescription?: string;
  /** Buyer pincode when address has none (export / e-way); max 10 chars */
  manualPincode?: string;
  grandTotal: number;
  grandTotalTax: number;
  totalAmount: number;
  status: InvoiceStatus | string;
  createdAt?: string;
  updatedAt?: string;

  // E-way bill transport details
  ewayTransporterId?: string;
  ewayTransporterName?: string;
  ewayDistanceKm?: number;
  ewayVehicleType?: string;
  ewayTransportMode?: string;

  // GSP e-invoice response payload fields
  signedInvoice?: string;
  signedQrcode?: string;
  einvoiceStatus?: string;
  ewayBillDate?: string;
  ewayBillValidTill?: string;
  einvoiceRemarks?: string;

  // Display fields
  branchName?: string;
  shipToName?: string;
  billToName?: string | null;
  createdByName?: string;
  approveByName?: string;
  signedByName?: string;

  // Related data
  invoiceDetails: InvoiceDetails[];
  otherCharges: OtherCharges[];
}

export interface InvoiceHeader extends InvoiceHeaderDto {
  // Some backends/GSPs return QR as different field names; keep them optional for UI rendering
  signedQRCode?: string;
  signedQrCode?: string;
  signedQRcode?: string;
  qrCode?: string;
  qrString?: string;

  /** FK to transporter master when invoice stores selected transporter */
  transporterMasterId?: number;

  // Backward compatibility for snake_case payload keys when present
  eway_transporter_id?: string;
  eway_transporter_name?: string;
  additional_remarks?: string;
  signed_by?: number;
  signed_on?: string;
}

export interface InvoiceHeaderListItem {
  id?: number;
  invoiceId?: number;
  invoiceHeaderId?: number;
  invoiceNo?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  billToName?: string | null;
}

export interface OtherCharges {
  id: number;
  invoiceId?: number;
  invoiceHeader?: InvoiceHeader;
  chargeMaster?: ChargeMaster;
  chargeMasterId?: number;
  amount: number;
  rate: number;
  sequenceOrder: number;
  createdAt?: string;
  updatedAt?: string;
  chargeName?: string;
  chargeCode?: string;
  chargeDescription?: string;
}

export interface ChargeMaster {
  id: number;
  applicableOn: string;
  chargeCode: string;
  chargeName: string;
  defaultAmountType: string;
  defaultRate: number;
  defaultType: string;
  description: string;
  effectiveStartDate: string;
  glAccountCode: string;
  taxable: boolean;
  createdAt: string;
  updatedAt: string;
}

// API Response types
export interface InvoiceHeaderResponse extends ApiResponse<InvoiceHeader[]> {}
export interface InvoiceDetailResponse extends ApiResponse<InvoiceDetails[]> {}
export interface ChargeMasterResponse extends ApiResponse<ChargeMaster[]> {}
export interface OtherChargesResponse extends ApiResponse<OtherCharges[]> {}

// DTOs for API operations
export interface InvoiceHeaderCreateRequest {
  branchId: number;
  /** Omit or null = same as invoice branch */
  shipFrom?: number | null;
  shipTo: number;
  billTo: number;
  createDate: string;
  createdBy: number;
  approveBy?: number;
  signedBy?: number;
  invoiceNo: string;
  invoiceDate: string;
  irn?: string;
  ackNo?: string;
  ackDate?: string;
  ewayBillNo?: string;
  deliverNote?: string;
  referenceNoAndDate?: string;
  otherReferences?: string;
  additionalRemarks?: string;
  additional_remarks?: string;
  buyerOrderNo?: string;
  buyerOrderDate?: string;
  dispatchDocNo?: string;
  deliverNoteDate?: string;
  dispatchedThrough?: string;
  ewayTransportMode?: string;
  ewayTransporterId?: string;
  ewayTransporterName?: string;
  ewayDistanceKm?: number;
  eway_transporter_id?: string;
  eway_transporter_name?: string;
  destination?: string;
  billOfLanding?: string;
  motorVehicleNo?: string;
  /** Optional when invoice is not tied to a sales order */
  salesOrderId?: number;
  transporterMasterId?: number;
  termOfDelivery?: string;
  /** e-invoice supply type (e.g. B2B, SEZWP, SEZWOP, EXPWP, EXPWOP, DEXP) */
  supTyp?: string;
  supplyType?: string;
  /** Export / buyer country (EXP supply types); backend field name may vary */
  exportCountryCode?: string;
  exportCountryId?: number;
  countryId?: number;
  portId?: number;
  manualPincode?: string;
  grandTotal: number;
  grandTotalTax: number;
  totalAmount: number;
  status: InvoiceStatus;
}

export interface InvoiceHeaderUpdateRequest extends Partial<InvoiceHeaderCreateRequest> {
  id: number;
}

// Complete invoice request with details and charges
export interface CompleteInvoiceCreateRequest extends InvoiceHeaderCreateRequest {
  invoiceDetails: InvoiceDetailsCreateRequest[];
  otherCharges: OtherChargesCreateRequest[];
}

export interface CompleteInvoiceUpdateRequest extends InvoiceHeaderUpdateRequest {
  invoiceDetails: InvoiceDetailsUpdateRequest[];
  otherCharges: OtherChargesUpdateRequest[];
}

export interface InvoiceDetailsCreateRequest {
  invoiceHeaderId: number;
  endgoodId?: number;
  /** ENDGOOD | RM */
  lineItemType?: string;
  rawItemId?: number;
  storeId?: number;
  heats?: InvoiceDetailHeatDto[];
  uom?: string;
  qty: number;
  rate: number;
  hsn?: string;
  cgst?: number;
  sgst?: number;
  igst?: number;
  workOrderId?: number;
  typeOfGood?: string;
  boxes?: string;
  packingDetailIds?: number[]; // Array of PackingDetail record IDs
  heatNo?: string;
  userProvidedHeatNo?: string;
  /** Set true for manual-entry invoice lines */
  isManual?: boolean;
  /** Set true for manual-entry invoice lines (DB column is_manual) */
  is_manual?: boolean;
}

export interface InvoiceDetailsUpdateRequest extends Partial<InvoiceDetailsCreateRequest> {
  id: number;
}

export interface OtherChargesCreateRequest {
  id?: number;
  amount: number;
  chargeMasterId: number;
  invoiceId: number;
  rate: number;
  sequenceOrder: number;
}

export interface OtherChargesUpdateRequest extends Partial<OtherChargesCreateRequest> {
  id: number;
}

export interface ChargeMasterCreateRequest {
  applicableOn?: string;
  chargeCode?: string;
  chargeName: string;
  defaultAmountType: string;
  defaultRate: number;
  defaultType: string;
  description?: string;
  effectiveStartDate: string;
  glAccountCode?: string;
  taxable: boolean;
}

export interface ChargeMasterUpdateRequest extends Partial<ChargeMasterCreateRequest> {
  id: number;
}

/** Cancel e-invoice request body (backend CancelEInvoiceRequestDto). */
export interface CancelEInvoiceRequest {
  irn: string;
  cnlrsn: string;
  cnlrem: string;
}

// Filters
export interface InvoiceHeaderFilters {
  branchId?: number;
  search?: string;
  status?: InvoiceStatus;
  sellerId?: number;
  billTo?: number;
  invoiceDateFrom?: string;
  invoiceDateTo?: string;
  createdBy?: number;
}

export interface InvoiceDetailsFilters {
  invoiceHeaderId?: number;
  endgoodId?: number;
  workOrderId?: number;
}

export interface OtherChargesFilters {
  invoiceHeaderId?: number;
  chargeMasterId?: number;
}

export interface ChargeMasterFilters {
  chargeCode?: string;
  chargeName?: string;
  taxable?: boolean;
  defaultType?: string;
}

// Invoice API Service
class InvoiceApiService extends BaseApiService {
  async getReferenceInvoices(branchId?: number): Promise<ApiResponse<InvoiceHeaderListItem[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<InvoiceHeaderListItem[]>>(
        API_ENDPOINTS.INVOICES.REFERENCE_LIST(branchId),
        { method: 'GET' }
      );

      return response;
    } catch (error) {
      throw error;
    }
  }
  
  // Get all invoice headers
  async getInvoiceHeaders(
    page: number = 1, 
    pageSize: number = 10,
    filters?: InvoiceHeaderFilters
  ): Promise<ApiResponse<InvoiceHeader[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        size: pageSize.toString(),
      });

      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && String(value).trim() !== '') {
            params.append(key, value.toString());
          }
        });
      }

      const response = await this.makeRequest<ApiResponse<InvoiceHeader[]>>(
        `${API_ENDPOINTS.INVOICES.LIST}?${params.toString()}`,
        { method: 'GET' }
      );
      
      return response;
    } catch (error) {
      // console.error('Error fetching invoice headers:', error);
      throw error;
    }
  }

  // Get invoice header by ID
  async getInvoiceHeaderById(id: string): Promise<ApiResponse<InvoiceHeader>> {
    try {
      const response = await this.makeRequest<ApiResponse<InvoiceHeader>>(
        API_ENDPOINTS.INVOICES.DETAIL(id),
        { method: 'GET' }
      );
      
      return response;
    } catch (error) {
      // console.error('Error fetching invoice header:', error);
      throw error;
    }
  }

  // Create invoice header
  async createInvoiceHeader(data: InvoiceHeaderCreateRequest): Promise<ApiResponse<InvoiceHeader>> {
    try {
      const response = await this.makeRequest<ApiResponse<InvoiceHeader>>(
        API_ENDPOINTS.INVOICES.CREATE,
        {
          method: 'POST',
          body: JSON.stringify(data)
        }
      );
      
      return response;
    } catch (error) {
      // console.error('Error creating invoice header:', error);
      throw error;
    }
  }

  // Create complete invoice with details and charges
  async createCompleteInvoice(data: CompleteInvoiceCreateRequest): Promise<ApiResponse<InvoiceHeader>> {
    try {
      const response = await this.makeRequest<ApiResponse<InvoiceHeader>>(
        API_ENDPOINTS.INVOICES.CREATE,
        {
          method: 'POST',
          body: JSON.stringify(data)
        }
      );
      
      return response;
    } catch (error) {
      // console.error('Error creating complete invoice:', error);
      throw error;
    }
  }

  // Update invoice header
  async updateInvoiceHeader(id: string, data: InvoiceHeaderUpdateRequest): Promise<ApiResponse<InvoiceHeader>> {
    try {
      const response = await this.makeRequest<ApiResponse<InvoiceHeader>>(
        API_ENDPOINTS.INVOICES.UPDATE(id),
        {
          method: 'PUT',
          body: JSON.stringify(data)
        }
      );
      
      return response;
    } catch (error) {
      // console.error('Error updating invoice header:', error);
      throw error;
    }
  }

  // Update complete invoice with details and charges
  async updateCompleteInvoice(id: string, data: CompleteInvoiceUpdateRequest): Promise<ApiResponse<InvoiceHeader>> {
    try {
      const response = await this.makeRequest<ApiResponse<InvoiceHeader>>(
        API_ENDPOINTS.INVOICES.UPDATE(id),
        {
          method: 'PUT',
          body: JSON.stringify(data)
        }
      );
      
      return response;
    } catch (error) {
      // console.error('Error updating complete invoice:', error);
      throw error;
    }
  }

  // Delete invoice header
  async deleteInvoiceHeader(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.INVOICES.DELETE(id),
        { method: 'DELETE' }
      );
      
      return response;
    } catch (error) {
      // console.error('Error deleting invoice header:', error);
      throw error;
    }
  }

  // Generate e-invoice (and optionally e-way bill)
  async generateEInvoice(id: string, ewayBillRequired: boolean = true): Promise<ApiResponse<InvoiceHeader>> {
    try {
      const response = await this.makeRequest<ApiResponse<InvoiceHeader>>(
        API_ENDPOINTS.INVOICES.GENERATE_EINVOICE(id, ewayBillRequired),
        {
          method: 'POST'
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  /** Generate e-way bill document (GENEWAYBILL) when invoice has IRN but no e-way bill yet. */
  async generateEWayBill(id: string): Promise<ApiResponse<InvoiceHeader>> {
    try {
      return await this.makeRequest<ApiResponse<InvoiceHeader>>(
        API_ENDPOINTS.INVOICES.GENERATE_EWAY_BILL(id),
        { method: 'POST' }
      );
    } catch (error) {
      throw error;
    }
  }

  /** Cancel e-invoice IRN via GSP (allowed within 24 hours of generation). */
  async cancelEInvoice(id: string, data: CancelEInvoiceRequest): Promise<ApiResponse<InvoiceHeader>> {
    try {
      return await this.makeRequest<ApiResponse<InvoiceHeader>>(
        API_ENDPOINTS.INVOICES.CANCEL_EINVOICE(id),
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    } catch (error) {
      throw error;
    }
  }

  // Search invoice headers
  async searchInvoiceHeaders(query: string): Promise<ApiResponse<InvoiceHeader[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<InvoiceHeader[]>>(
        `${API_ENDPOINTS.INVOICES.SEARCH}?q=${encodeURIComponent(query)}`,
        { method: 'GET' }
      );
      
      return response;
    } catch (error) {
      // console.error('Error searching invoice headers:', error);
      throw error;
    }
  }

  // Get invoice details by header ID
  async getInvoiceDetailsByHeader(headerId: string): Promise<ApiResponse<InvoiceDetails[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<InvoiceDetails[]>>(
        API_ENDPOINTS.INVOICES.DETAILS_BY_HEADER(headerId),
        { method: 'GET' }
      );
      
      return response;
    } catch (error) {
      // console.error('Error fetching invoice details:', error);
      throw error;
    }
  }

  // Create invoice detail
  async createInvoiceDetail(data: InvoiceDetailsCreateRequest): Promise<ApiResponse<InvoiceDetails>> {
    try {
      const response = await this.makeRequest<ApiResponse<InvoiceDetails>>(
        API_ENDPOINTS.INVOICES.CREATE_DETAIL,
        {
          method: 'POST',
          body: JSON.stringify(data)
        }
      );
      
      return response;
    } catch (error) {
      // console.error('Error creating invoice detail:', error);
      throw error;
    }
  }

  // Update invoice detail
  async updateInvoiceDetail(id: string, data: InvoiceDetailsUpdateRequest): Promise<ApiResponse<InvoiceDetails>> {
    try {
      const response = await this.makeRequest<ApiResponse<InvoiceDetails>>(
        API_ENDPOINTS.INVOICES.UPDATE_DETAIL(id),
        {
          method: 'PUT',
          body: JSON.stringify(data)
        }
      );
      
      return response;
    } catch (error) {
      // console.error('Error updating invoice detail:', error);
      throw error;
    }
  }

  // Delete invoice detail
  async deleteInvoiceDetail(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.INVOICES.DELETE_DETAIL(id),
        { method: 'DELETE' }
      );
      
      return response;
    } catch (error) {
      // console.error('Error deleting invoice detail:', error);
      throw error;
    }
  }

  // Get charge master by ID (handles both raw charge and wrapped { data: charge } responses)
  async getChargeMasterById(id: string): Promise<ApiResponse<ChargeMaster>> {
    try {
      const response = await this.makeRequest<ChargeMaster | { data?: ChargeMaster }>(
        API_ENDPOINTS.INVOICES.CHARGE_MASTER_BY_ID(id),
        { method: 'GET' }
      );
      const data = response && typeof response === 'object' && 'data' in response && response.data
        ? response.data
        : (response as ChargeMaster);
      return { success: true, message: 'Charge master fetched', data };
    } catch (error) {
      throw error;
    }
  }

  // Get all charge masters
  async getChargeMasters(
    page: number = 1, 
    pageSize: number = 10,
    filters?: ChargeMasterFilters
  ): Promise<ApiResponse<ChargeMaster[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        size: pageSize.toString(),
      });

      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
      }

      const response = await this.makeRequest<ApiResponse<ChargeMaster[]>>(
        `${API_ENDPOINTS.INVOICES.CHARGE_MASTERS}?${params.toString()}`,
        { method: 'GET' }
      );
      
      return response;
    } catch (error) {
      // console.error('Error fetching charge masters:', error);
      throw error;
    }
  }

  // Create charge master
  async createChargeMaster(data: ChargeMasterCreateRequest): Promise<ApiResponse<ChargeMaster>> {
    try {
      const response = await this.makeRequest<ApiResponse<ChargeMaster>>(
        API_ENDPOINTS.INVOICES.CREATE_CHARGE_MASTER,
        {
          method: 'POST',
          body: JSON.stringify(data)
        }
      );
      
      return response;
    } catch (error) {
      // console.error('Error creating charge master:', error);
      throw error;
    }
  }

  // Update charge master
  async updateChargeMaster(id: string, data: ChargeMasterUpdateRequest): Promise<ApiResponse<ChargeMaster>> {
    try {
      const response = await this.makeRequest<ApiResponse<ChargeMaster>>(
        API_ENDPOINTS.INVOICES.UPDATE_CHARGE_MASTER(id),
        {
          method: 'PUT',
          body: JSON.stringify(data)
        }
      );
      
      return response;
    } catch (error) {
      // console.error('Error updating charge master:', error);
      throw error;
    }
  }

  // Delete charge master
  async deleteChargeMaster(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.INVOICES.DELETE_CHARGE_MASTER(id),
        { method: 'DELETE' }
      );
      
      return response;
    } catch (error) {
      // console.error('Error deleting charge master:', error);
      throw error;
    }
  }

  // Get other charges by invoice header ID
  async getOtherChargesByInvoice(headerId: string): Promise<ApiResponse<OtherCharges[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<OtherCharges[]>>(
        API_ENDPOINTS.INVOICES.OTHER_CHARGES_BY_INVOICE(headerId),
        { method: 'GET' }
      );
      
      return response;
    } catch (error) {
      // console.error('Error fetching other charges:', error);
      throw error;
    }
  }

  // Create other charge
  async createOtherCharge(data: OtherChargesCreateRequest): Promise<ApiResponse<OtherCharges>> {
    try {
      const response = await this.makeRequest<ApiResponse<OtherCharges>>(
        API_ENDPOINTS.INVOICES.CREATE_OTHER_CHARGE,
        {
          method: 'POST',
          body: JSON.stringify(data)
        }
      );
      
      return response;
    } catch (error) {
      // console.error('Error creating other charge:', error);
      throw error;
    }
  }

  // Update other charge
  async updateOtherCharge(id: string, data: OtherChargesUpdateRequest): Promise<ApiResponse<OtherCharges>> {
    try {
      const response = await this.makeRequest<ApiResponse<OtherCharges>>(
        API_ENDPOINTS.INVOICES.UPDATE_OTHER_CHARGE(id),
        {
          method: 'PUT',
          body: JSON.stringify(data)
        }
      );
      
      return response;
    } catch (error) {
      // console.error('Error updating other charge:', error);
      throw error;
    }
  }

  // Delete other charge
  async deleteOtherCharge(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.INVOICES.DELETE_OTHER_CHARGE(id),
        { method: 'DELETE' }
      );
      
      return response;
    } catch (error) {
      // console.error('Error deleting other charge:', error);
      throw error;
    }
  }
}

// Export the service instance
export const invoiceApiService = new InvoiceApiService();
